import flatten from "lodash/flatten";
import { useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "react-query";

import type { GetMyConnectionsResponse } from "@/react/api/myConnections/myConnectionsApi";
import { myConnectionsApi } from "@/react/api/myConnections/myConnectionsApi";
import type { CommunityMember } from "@/react/types/CommunityMember";

const DEFAULT_PAGE_SIZE = 10;
const MY_CONNECTIONS_QUERY_KEY = "my-connections";

interface UseMyConnectionsInfiniteQueryProps {
  initialPage?: number;
  options?: object;
  perPage?: number;
}

export const useMyConnectionsInfiniteQuery = ({
  perPage = DEFAULT_PAGE_SIZE,
  initialPage = 1,
  options = {},
}: UseMyConnectionsInfiniteQueryProps = {}) => {
  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    return myConnectionsApi.getMyConnections({
      page,
      per_page: perPage,
    });
  };

  const queryKey = [MY_CONNECTIONS_QUERY_KEY, perPage, initialPage];

  const queryOptions = {
    getNextPageParam: (lastPage: GetMyConnectionsResponse) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: GetMyConnectionsResponse) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    placeholderData: keepPreviousData,
    ...options,
  };

  const { data, fetchNextPage, hasNextPage, isPending } = useInfiniteQuery({
    initialPageParam: initialPage,
    queryKey,
    queryFn: fetchPage,
    ...queryOptions,
  });
  const pages = useMemo(() => data?.pages || [], [data?.pages]);
  const totalCount = pages[0]?.count || 0;
  const members: CommunityMember[] = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );

  return {
    fetchNextPage,
    hasNextPage,
    isPending,
    totalCount,
    members,
  };
};
